import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Reveal } from '../../ui/Reveal/Reveal';
import { LiquidImage } from '../../ui/LiquidImage/LiquidImage';
import { TextScramble } from '../../ui/TextScramble/TextScramble';
import { categories } from '../../../data/categories';
import { SocialPaymentModal } from '../../ui/SocialPaymentModal/SocialPaymentModal';
import { CorporatePaymentModal } from '../../ui/CorporatePaymentModal/CorporatePaymentModal';
import './Categories.css';

export function Categories() {
  const [active, setActive] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isModalOpen, setIsModalOpen] = useState(searchParams.get('modal') === 'social');
  const [isCorporateModalOpen, setIsCorporateModalOpen] = useState(searchParams.get('modal') === 'corporate');
  const [gridRef] = useAutoAnimate<HTMLDivElement>({ duration: 400, easing: 'ease-in-out' });

  useEffect(() => {
    if (searchParams.get('modal') === 'social') {
      setIsModalOpen(true);
      setIsCorporateModalOpen(false);
    } else if (searchParams.get('modal') === 'corporate') {
      setIsCorporateModalOpen(true);
      setIsModalOpen(false);
    } else {
      setIsModalOpen(false);
      setIsCorporateModalOpen(false);
    }
  }, [searchParams]);

  const handleCardClick = (id: string) => {
    if (id === 'social' || id === 'corporate') {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('modal', id);
      setSearchParams(newParams);
    }
  };

  const handleCloseModal = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('modal');
    setSearchParams(newParams);
  };

  return (
    <>
      <section id="categories" className="section categories">
        <div className="container">
          <div className="categories__head">
            <Reveal>
              <span className="eyebrow">Eight Practices</span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="categories__title display-lg">
                Every kind of event.<br />
                <span className="italic-accent">One standard of craft.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="categories__lede">
                From boardrooms to ballrooms, stadiums to sanctuaries — Luminar produces across
                eight disciplines, each with its own dedicated lead and runbook.
              </p>
            </Reveal>
          </div>

          <div ref={gridRef} className="categories__grid">
            {categories.map((c, i) => (
              <motion.article
                key={c.id}
                className={`cat-card ${active === c.id ? 'cat-card--active' : ''}`}
                onMouseEnter={() => setActive(c.id)}
                onMouseLeave={() => setActive(null)}
                onClick={() => handleCardClick(c.id)}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.9, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                data-cursor={c.id === 'social' || c.id === 'corporate' ? 'pointer' : 'hover'}
                data-cursor-label={c.id === 'social' ? 'Donate' : c.id === 'corporate' ? 'Pay' : `View ${c.name}`}
              >
                <div className="cat-card__media">
                  <LiquidImage src={c.image} alt={c.name} />
                  <div className="cat-card__overlay" />
                  <span className="cat-card__count">{c.count}</span>
                </div>

                <div className="cat-card__body">
                  <div className="cat-card__title-row">
                    <h3 className="cat-card__title">
                      <TextScramble text={c.name} />
                    </h3>
                    <span className="cat-card__arrow">
                      <ArrowUpRight size={18} strokeWidth={1.5} />
                    </span>
                  </div>
                  <p className="cat-card__tagline">{c.tagline}</p>
                  <div className="cat-card__rule" />
                  <p className="cat-card__desc">{c.description}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {isModalOpen && (
        <SocialPaymentModal 
          isOpen={true} 
          onClose={handleCloseModal} 
        />
      )}
      {isCorporateModalOpen && (
        <CorporatePaymentModal 
          isOpen={true} 
          onClose={handleCloseModal} 
        />
      )}
    </>
  );
}
